import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { RESOURCE_TYPES } from './aoShared'

const emptyForm = { rs_type: 'room', rs_name: '', rs_detail: '', rs_active: true }

// ธุรการ: จัดการทรัพยากรที่เปิดให้จอง (ห้องประชุม / รถโรงเรียน)
export default function ResourceManage() {
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get('/api/ao/resources?all=1')
      if (res.data.success) setResources(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const openCreate = () => {
    setEditId(null)
    setForm(emptyForm)
    setModalError('')
    setShowModal(true)
  }

  const openEdit = (r) => {
    setEditId(r.rs_id)
    setForm({ rs_type: r.rs_type, rs_name: r.rs_name, rs_detail: r.rs_detail || '', rs_active: !!r.rs_active })
    setModalError('')
    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setModalError('')
    try {
      if (editId) await axiosInstance.put(`/api/ao/resources/${editId}`, form)
      else await axiosInstance.post('/api/ao/resources', form)
      setShowModal(false)
      fetchData()
    } catch (err) {
      setModalError(err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-building-line me-2 text-primary" />
              ห้องประชุม / รถโรงเรียน
            </h4>
            <Button variant="primary" size="sm" onClick={openCreate}>
              <i className="ri-add-line me-1" />เพิ่มทรัพยากร
            </Button>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <Card className="card-one">
              <Card.Body className="p-0">
                {resources.length === 0 ? (
                  <div className="text-center text-muted py-4">ยังไม่มีทรัพยากร กด "เพิ่มทรัพยากร" เพื่อเริ่มต้น</div>
                ) : (
                  <Table responsive hover className="mb-0 fs-sm">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 130 }}>ประเภท</th>
                        <th>ชื่อ</th>
                        <th>รายละเอียด</th>
                        <th style={{ width: 110 }}>สถานะ</th>
                        <th style={{ width: 70 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {resources.map(r => {
                        const rt = RESOURCE_TYPES[r.rs_type] || RESOURCE_TYPES.room
                        return (
                          <tr key={r.rs_id}>
                            <td><Badge bg={rt.bg}>{rt.label}</Badge></td>
                            <td className="fw-semibold">{r.rs_name}</td>
                            <td>{r.rs_detail || '-'}</td>
                            <td>
                              {r.rs_active
                                ? <Badge bg="success">เปิดให้จอง</Badge>
                                : <Badge bg="secondary">ปิดใช้งาน</Badge>}
                            </td>
                            <td className="text-end">
                              <Button size="sm" variant="outline-primary" title="แก้ไข" onClick={() => openEdit(r)}>
                                <i className="ri-edit-line" />
                              </Button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>
      <Footer />

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">{editId ? 'แก้ไขทรัพยากร' : 'เพิ่มทรัพยากร'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
            <Row className="g-2 mb-2">
              <Col xs={5}>
                <Form.Label className="fs-sm mb-1">ประเภท</Form.Label>
                <Form.Select size="sm" value={form.rs_type}
                  onChange={e => setForm(f => ({ ...f, rs_type: e.target.value }))}>
                  {Object.entries(RESOURCE_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </Form.Select>
              </Col>
              <Col xs={7}>
                <Form.Label className="fs-sm mb-1">ชื่อ <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="text" maxLength={255} required value={form.rs_name}
                  placeholder={form.rs_type === 'room' ? 'เช่น ห้องประชุมใหญ่ อาคาร 1' : 'เช่น รถตู้ 1 (นข-1234)'}
                  onChange={e => setForm(f => ({ ...f, rs_name: e.target.value }))} />
              </Col>
            </Row>
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">รายละเอียด <span className="text-muted">— ความจุ/ทะเบียน/หมายเหตุ</span></Form.Label>
              <Form.Control size="sm" type="text" maxLength={500} value={form.rs_detail}
                onChange={e => setForm(f => ({ ...f, rs_detail: e.target.value }))} />
            </Form.Group>
            {editId && (
              <Form.Check type="switch" id="rs-active" label="เปิดให้จอง"
                checked={form.rs_active}
                onChange={e => setForm(f => ({ ...f, rs_active: e.target.checked }))} />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line me-1" />}
              บันทึก
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
